import styles from './index.module.less';
import { App, Button, DatePicker, Dropdown, MenuProps, Radio, RadioChangeEvent, Space } from 'antd';
import { useUserProvider } from '@/context/UserProvider';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useConfigProvider } from '@/context/ConfigProvider';
import BillingRecordsItem from './BillingRecordsItem';
import classNames from 'classnames';
import { ActionType, ProList } from '@ant-design/pro-components';
import { getOrderListSearchData } from '@/lib/service';
import { PaymentHistoryModel } from '@/types/home';
import dayjs from 'dayjs';
import SettingsEmptyContent from '../SettingsEmpty';
import { ChevronDown } from 'lucide-react';

const { RangePicker } = DatePicker;

const RecordsContent = ({ data }: { data?: any }) => {
  const { isLogin } = useUserProvider();
  const router = useRouter();
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const { message } = App.useApp();
  const { isBlogWeb } = useConfigProvider();
  const [dataList, setDataList] = useState<PaymentHistoryModel[]>();
  const [dateValue, setDateValue] = useState(1);
  const [dateHover, setDateHover] = useState(false);
  const [dateDivHover, setDateDivHover] = useState(false);
  const actionRef = useRef<ActionType>();
  const [typeParams, setTypeParams] = useState<string>('');
  const [dateParams, setDateParams] = useState<any>({
    begin_time: dayjs().startOf('month').format('YYYY-MM-DD'),
    end_time: dayjs().format('YYYY-MM-DD'),
  });

  useEffect(() => {
    if (isBlogWeb) {
      router.push('/instagram-tracking-blog');
    }
  }, []);

  useEffect(() => {
    if (actionRef.current) {
      actionRef.current.pageInfo.current = 1;
      actionRef.current.reload();
    }
  }, [typeParams]);

  useEffect(() => {
    if (actionRef.current) {
      actionRef.current.pageInfo.current = 1;
      actionRef.current.reload();
    }
  }, [dateParams]);

  const onDateChange = (e: RadioChangeEvent) => {
    setDateValue(e?.target?.value);
    const tempval = e?.target?.value;

    var startDate: string;
    var endDate: string;

    if (tempval === 1) {
      const firstDayOfMonth = dayjs().startOf('month');
      startDate = firstDayOfMonth.format('YYYY-MM-DD');
      endDate = dayjs().format('YYYY-MM-DD');
    } else if (tempval === 2) {
      startDate = dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
      endDate = dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
    } else if (tempval === 3) {
      startDate = dayjs().subtract(90, 'day').format('YYYY-MM-DD');
      endDate = dayjs().format('YYYY-MM-DD');
    } else if (tempval === 4) {
      const firstDayOfYear = dayjs().startOf('year');
      startDate = firstDayOfYear.format('YYYY-MM-DD');
      endDate = dayjs().format('YYYY-MM-DD');
    } else if (tempval === 5) {
      startDate = dayjs().subtract(1, 'year').startOf('year').format('YYYY-MM-DD');
      endDate = dayjs().subtract(1, 'year').endOf('year').format('YYYY-MM-DD');
    }

    setDateParams({ begin_time: startDate, end_time: endDate });

    setDateDivHover(false);
    setDateHover(false);
  };

  const handleDateRangeChange = (values: any) => {
    if (values?.length > 1) {
      setDateValue(6);

      setDateParams({
        begin_time: values[0].format('YYYY-MM-DD'),
        end_time: values[1].format('YYYY-MM-DD'),
      });

      setDateDivHover(false);
      setDateHover(false);
    }
  };

  const selectedColor = (selected: boolean) => {
    return { color: selected ? `var(--app-primary-color)` : `var(--app-text-color)` };
  };

  const items: MenuProps['items'] = [
    {
      key: 'all',
      label: (
        <div
          style={selectedColor(typeParams === '')}
          onClick={() => {
            setTypeParams('');
          }}
        >
          {t('All')}
        </div>
      ),
    },
    {
      key: 'one_time',
      label: (
        <div
          style={selectedColor(typeParams === 'one_time')}
          onClick={() => {
            setTypeParams('one_time');
          }}
        >
          {t('OneTimePurchase')}
        </div>
      ),
    },
    {
      key: 'subscription',
      label: (
        <div
          style={selectedColor(typeParams === 'subscription')}
          onClick={() => {
            setTypeParams('subscription');
          }}
        >
          {t('Subscription')}
        </div>
      ),
    },
  ];

  const dateItems: MenuProps['items'] = [
    {
      key: 'all',
      label: (
        <div>
          <div className={styles.alertTitle}>{t('PaymentDate')}</div>
          <Radio.Group onChange={onDateChange} value={dateValue}>
            <Space direction="vertical">
              <Radio value={1}>{t('ThisMonth')}</Radio>
              <Radio value={2}>{t('LastMonth')}</Radio>
              <Radio value={3}>{t('Last_90_Days')}</Radio>
              <Radio value={4}>{t('ThisYear')}</Radio>
              <Radio value={5}>{t('LastYear')}</Radio>
            </Space>
          </Radio.Group>
        </div>
      ),
    },
    {
      key: 'one_time',
      label: (
        <div onClick={(e) => e.stopPropagation()}>
          <div className={styles.alertTitle}>{t('SelectDateRange')}</div>
          <RangePicker style={{ zIndex: '100' }} onChange={handleDateRangeChange} />
        </div>
      ),
    },
  ];

  const getDateStr = () => {
    if (dateValue === 1) {
      return t('ThisMonth');
    } else if (dateValue === 2) {
      return t('LastMonth');
    } else if (dateValue === 3) {
      return t('Last_90_Days');
    } else if (dateValue === 4) {
      return t('ThisYear');
    } else if (dateValue === 5) {
      return t('LastYear');
    } else {
      return `${dateParams?.begin_time}~${dateParams?.end_time}`;
    }
  };

  const contentContainer = () => {
    return (
      <div className={styles.content}>
        <h2 className={styles.topContainer}>{t('PaymentHistory')}</h2>

        <div className={styles.toolsContent}>
          <Dropdown menu={{ items: dateItems }} placement="bottomLeft" autoAdjustOverflow={false}>
            <Button className={classNames([styles.btn, styles.dateBtn])} id="dateId">
              <span>{`${t('Date')}: ${getDateStr()}`}</span>
              <ChevronDown />
            </Button>
          </Dropdown>

          <Dropdown menu={{ items }} placement="bottomLeft" autoAdjustOverflow={false}>
            <Button className={styles.btn}>
              <span>{t('PaymentType')}</span>
              <ChevronDown />
            </Button>
          </Dropdown>
        </div>

        <div className={styles.mainContent}>
          <ProList<any>
            actionRef={actionRef}
            ghost={true}
            pagination={{
              defaultPageSize: 12,
              showSizeChanger: false,
              align: 'center',
              showTotal: null,
            }}
            showActions="always"
            grid={{ gutter: 0, column: 1 }}
            itemCardProps={{
              style: {
                width: '100%',
                height: '100%',
                border: 0,
                background: `var(--foreground-rgb)`,
              },
              bodyStyle: {
                margin: 0,
                padding: 0,
              },
            }}
            metas={{
              content: {
                render: (_, reacd: PaymentHistoryModel) => <BillingRecordsItem data={reacd} />,
              },
            }}
            request={async (params: any) => {
              const res = await getOrderListSearchData(
                {
                  ...dateParams,
                  type: typeParams,
                  page_no: params.current,
                  page_size: params.pageSize,
                },
                router.locale
              );
              if (res?.code === 40001) {
                router.replace('/');
              } else if (res?.code != 0) {
                message.error(res?.message);
              }
              setDataList(res?.data);

              window.scrollTo({ top: 0, behavior: 'smooth' });

              return {
                ...res,
                data: res.data,
              };
            }}
          />

          <div className={styles.empty} style={{ display: initLoading && dataList?.length === 0 ? 'block' : 'none' }}>
            <SettingsEmptyContent
              title={t('No Payment Records Found')}
              desction={t(
                "It looks like you haven’t started tracking any accounts yet! <a href='1xxx1' style='color:#725CFF;fontWeight:600'>Add an account</a> to unlock insights into someone’s Instagram activity today!"
              ).replace('1xxx1', '/dashboard?alertType=1')}
            />
          </div>
        </div>
      </div>
    );
  };

  if (isBlogWeb) {
    return <></>;
  }

  return (
    <div
      onClick={() => {
        if (dateHover || dateDivHover) {
          setDateHover(false);
          setDateDivHover(false);
        }
      }}
    >
      {contentContainer()}
    </div>
  );
};

export default RecordsContent;
